import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Users, User } from 'lucide-react';

export default function HoursByClientTable({ horas, tickets, cardClasses }) {
  const records = Array.isArray(horas) ? horas : (Array.isArray(tickets) ? tickets : []);

  const toUpper = (v) => (v ?? '').toString().trim().toUpperCase();

  const hasTicket = (v) => {
    const s = (v ?? '').toString().trim();
    return s !== '' && s.toLowerCase() !== 'null' && s.toLowerCase() !== 'undefined';
  };

  const hhmmssToHours = (str) => {
    if (!str) return 0;
    const parts = str.toString().split(':').map(Number);
    const h = parts[0] || 0;
    const m = parts[1] || 0;
    const s = parts[2] || 0;
    return h + m / 60 + s / 3600;
  };

  // Filtrar: solo registros con ticket y Equipo=F2F
  const filtered = records.filter((r) => {
    const equipoVal = r.Equipo ?? r.equipo;
    return hasTicket(r.ticket) && toUpper(equipoVal) === 'F2F';
  });

  // Agrupación: cliente → técnicos → horas
  const grouped = filtered.reduce((acc, r) => {
    const cliente = (r.proyecto ?? r.Proyecto ?? r.cliente ?? r.Cliente ?? 'Sin proyecto') || 'Sin proyecto';
    const tecnico = r.tecnico ?? r.Tecnico ?? 'Sin técnico';

    let horasItem = 0;

    if (r.horas !== null && r.horas !== undefined && r.horas !== '') {
      horasItem = Number(r.horas) || 0;
    } else if (r.tiempo_empleado !== null && r.tiempo_empleado !== undefined && r.tiempo_empleado !== '') {
      if (typeof r.tiempo_empleado === 'number') {
        horasItem = r.tiempo_empleado / 60;
      } else {
        const tiempoStr = r.tiempo_empleado.toString();

        if (tiempoStr.includes(':')) {
          horasItem = hhmmssToHours(tiempoStr);
        } else {
          horasItem = (Number(tiempoStr) || 0) / 60;
        }
      }
    } else if (r.Horas_Registradas !== null && r.Horas_Registradas !== undefined && r.Horas_Registradas !== '') {
      horasItem = Number(r.Horas_Registradas) || 0;
    }

    if (!acc[cliente]) acc[cliente] = { total: 0, tecnicos: {} };
    acc[cliente].total += horasItem;
    acc[cliente].tecnicos[tecnico] = (acc[cliente].tecnicos[tecnico] || 0) + horasItem;

    return acc;
  }, {});

  const rows = Object.entries(grouped)
    .map(([cliente, data]) => ({
      cliente,
      horas: data.total,
      tecnicos: data.tecnicos,
    }))
    .sort((a, b) => b.horas - a.horas);

  const totalHoras = rows.reduce((s, r) => s + r.horas, 0);

  // Estado de clientes expandidos
  const [expanded, setExpanded] = useState({});
  const toggleExpand = (cliente) => {
    setExpanded((prev) => ({ ...prev, [cliente]: !prev[cliente] }));
  };

  return (
    <div className={`${cardClasses} p-6 rounded-2xl mb-8`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold">Consumo de Horas por Cliente (F2F)</h2>
        <div className="opacity-80 text-sm">
          Total: <span className="font-semibold">{totalHoras.toFixed(2)} h</span>
        </div>
      </div>

      {/* Contenedor con altura fija y scroll */}
      <div className="h-96 overflow-y-auto border border-gray-600/30 rounded-lg bg-black/20">
        <table className="w-full border-collapse">
          {/* Header sticky */}
          <thead className="sticky top-0 bg-gray-800/90 backdrop-blur-sm">
            <tr className="text-left border-b border-gray-500/30">
              <th className="p-3">Cliente</th>
              <th className="p-3">Horas de Atención en Tickets</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <React.Fragment key={idx}>
                {/* Fila de cliente con badge y contador de técnicos */}
                <tr
                  className="border-b border-gray-500/20 cursor-pointer hover:bg-white/10 transition-colors"
                  onClick={() => toggleExpand(row.cliente)}
                >
                  <td className="p-3 font-semibold">
                    <div className="flex items-center gap-2">
                      <div className="transition-transform duration-200">
                        {expanded[row.cliente] ? 
                          <ChevronDown size={16} className="text-blue-400" /> : 
                          <ChevronRight size={16} className="text-gray-400" />
                        }
                      </div>
                      <span className="mr-2">{row.cliente}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium transition-colors duration-200 ${
                        expanded[row.cliente] 
                          ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                          : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                      }`}>
                        <Users size={10} className="inline mr-1" />
                        {Object.keys(row.tecnicos).length}
                      </span>
                    </div>
                  </td>
                  <td className="p-3">{row.horas.toFixed(2)} h</td>
                </tr>

                {/* Filas de técnicos (solo si expandido) */}
                {expanded[row.cliente] &&
                  Object.entries(row.tecnicos)
                    .sort((a, b) => b[1] - a[1])
                    .map(([tecnico, horasT], i) => (
                      <tr key={`${idx}-${i}`} className="border-b border-gray-500/10">
                        <td className="p-3 pl-8 text-sm opacity-90">
                          <div className="flex items-center gap-2">
                            <User size={14} className="opacity-60" />
                            {tecnico}
                          </div>
                        </td>
                        <td className="p-3 text-sm">{horasT.toFixed(2)} h</td>
                      </tr>
                    ))}
              </React.Fragment>
            ))}

            {rows.length === 0 && (
              <tr>
                <td colSpan="2" className="p-3 text-center opacity-60">
                  No hay registros disponibles
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}